/**
 * SortableNavGroup — generic drag-to-reorder nav row list for the Sidebar.
 * Extracted from AdminModuleNavGroup (2026-09-05) so any admin nav group
 * (Modules, Admin Tools, …) can offer the same reorder-via-drag UX,
 * persisted per browser under its own localStorage key.
 */
import { useCallback, useState } from "react";
import { NavLink } from "react-router-dom";
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { MR } from "@/components/Sidebar";

function readOrder(storageKey, defaultOrder) {
    if (typeof window === "undefined") return defaultOrder;
    try {
        const raw = window.localStorage.getItem(storageKey);
        const parsed = raw ? JSON.parse(raw) : null;
        if (!Array.isArray(parsed)) return defaultOrder;
        const set = new Set(defaultOrder);
        const kept = parsed.filter((id) => set.has(id));
        const additions = defaultOrder.filter((id) => !kept.includes(id));
        return [...kept, ...additions];
    } catch {
        return defaultOrder;
    }
}

function SortableRow({ item, collapsed, onNavigate }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 10 : "auto",
    };
    const Icon = item.icon;

    const row = (
        <div ref={setNodeRef} style={style} className="group relative" data-testid={`sortable-nav-row-${item.id}`}>
            <NavLink
                to={item.to}
                onClick={onNavigate}
                data-testid={item.testId}
                className="outline-none"
                style={({ isActive }) => ({
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "0.375rem",
                    fontWeight: 500,
                    minHeight: "44px",
                    ...(collapsed
                        ? { justifyContent: "center", width: 44, height: 44, margin: "0 auto" }
                        : { gap: "0.625rem", padding: "0.5rem 1.75rem 0.5rem 0.75rem" }),
                    color: isActive ? MR.TEXT_PRIMARY : MR.TEXT_SECONDARY,
                    background: isActive ? MR.ACTIVE_BG : "transparent",
                    borderLeft: isActive ? `4px solid ${MR.ACTIVE_BORDER}` : "4px solid transparent",
                })}
            >
                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                {!collapsed && <span className="truncate text-sm flex-1">{item.label}</span>}
            </NavLink>
            {!collapsed && (
                <button
                    type="button"
                    {...attributes}
                    {...listeners}
                    data-testid={`${item.testId}-drag-handle`}
                    aria-label={`Reorder ${item.label}`}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md opacity-0 group-hover:opacity-100 focus:opacity-100 cursor-grab active:cursor-grabbing transition-opacity"
                    style={{ color: MR.TEXT_SECONDARY }}
                >
                    <GripVertical className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
            )}
        </div>
    );

    if (!collapsed) return row;
    return (
        <Tooltip>
            <TooltipTrigger asChild>{row}</TooltipTrigger>
            <TooltipContent
                side="right"
                className="text-caption font-heading border"
                style={{ background: "#0F172A", color: "#F8FAFC", borderColor: "rgba(255,255,255,0.12)" }}
            >
                {item.label}
            </TooltipContent>
        </Tooltip>
    );
}

/**
 * @param storageKey  localStorage key the drag order persists under.
 * @param items       [{ id, to, label, icon, testId }] in default order.
 */
export default function SortableNavGroup({ storageKey, items, collapsed, onNavigate }) {
    const byId = Object.fromEntries(items.map((i) => [i.id, i]));
    const defaultOrder = items.map((i) => i.id);
    const [order, setOrder] = useState(() => readOrder(storageKey, defaultOrder));

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = useCallback(({ active, over }) => {
        if (!over || active.id === over.id) return;
        setOrder((prev) => {
            const oldIndex = prev.indexOf(active.id);
            const newIndex = prev.indexOf(over.id);
            if (oldIndex < 0 || newIndex < 0) return prev;
            const next = arrayMove(prev, oldIndex, newIndex);
            try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    }, [storageKey]);

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={order} strategy={verticalListSortingStrategy}>
                <div className="space-y-1.5" data-testid={`${storageKey}-sortable-list`}>
                    {order.map((id) => byId[id] && (
                        <SortableRow key={id} item={byId[id]} collapsed={collapsed} onNavigate={onNavigate} />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}
